/**
 * LOGS — read and download what a till uploaded.
 *
 * The backend already keeps every `collect_logs` / `capture_screen` upload in
 * S3 for 7 days and hands out a signed link per file
 * (`GET /fleet/commands/{id}/artifact`). Before this tab nobody could read
 * them without the AWS console. Left: the captures (this till's commands,
 * filtered to those two actions). Right: the file itself — log lines with
 * numbers, coloured by Android level, filterable, copyable, downloadable.
 *
 * DELIVERY IS A LIVE PUSH, not "on the next check-in": the command goes down
 * the till's open connection and the upload usually lands in seconds. A till
 * that is offline gets it the moment it reconnects (the command waits 24 h).
 *
 * SIZE: a 30-minute logcat is up to ~2 MB ≈ 20,000 lines. Rendering all of
 * them at once locks the tab, so the viewer renders the first 5,000 matching
 * lines and offers "Show more"; filter, copy and download always work on the
 * whole file.
 */

import { useCallback, useDeferredValue, useEffect, useMemo, useState } from 'react';
import { AlertTriangle, Copy, Download, FileText, Image as ImageIcon, Search, WrapText } from 'lucide-react';
import type { FleetCommand } from '@platform/shared-types';
import { isTerminalState } from '@platform/shared-types';
import { fleetSupportApi } from '../../../services/fleetSupportApi';
import { toastService } from '../../../services/toastService';
import ShimmerImage from '../../../components/ShimmerImage';
import { RubyLoader } from '../../../components/RubyLoader';
import { TONE_PILL, type Tone } from '../fleetStatus';
import { formatWhen, relativeTime, type DeviceTabProps } from '../../pos/device/deviceDetail';
import type { FleetRow } from '../../pos/fleet/fleetRows';
import {
  captureFileName, captureSize, copyText, filterLines, isCapture, isExpiredCapture, levelClass,
  parseLogcat, saveFile, type LevelFilter, type LogLine,
} from './logcat';

const LIST_LIMIT = 50;
const POLL_MS = 3000;
/** Poll a fresh capture for up to two minutes, then stop and let the operator refresh. */
const POLL_WINDOW_MS = 2 * 60 * 1000;
/** Lines rendered per "page" — the file can be ~20k lines. */
export const RENDER_STEP = 5000;

const KIND_LABEL: Record<string, string> = { collect_logs: 'Logs', capture_screen: 'Screenshot' };

export function capturePill(c: FleetCommand, now: number = Date.now()): { label: string; tone: Tone } {
  switch (c.state) {
    case 'queued':
    case 'sent':
    case 'acked':
      return { label: 'Waiting for till', tone: 'info' };
    case 'done':
      return isExpiredCapture(c, now) ? { label: 'Expired', tone: 'mute' } : { label: 'Ready', tone: 'ok' };
    case 'failed': return { label: 'Failed', tone: 'bad' };
    case 'expired': return { label: 'Timed out', tone: 'bad' };
    case 'cancelled': return { label: 'Cancelled', tone: 'mute' };
    default: return { label: c.state, tone: 'mute' };
  }
}

function Pill({ tone, children }: { tone: Tone; children: React.ReactNode }) {
  return (
    <span className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-semibold ${TONE_PILL[tone]}`}>
      {children}
    </span>
  );
}

export default function LogsTab({ row, freshCommand }: DeviceTabProps) {
  const deviceId = row.deviceId;
  const [captures, setCaptures] = useState<FleetCommand[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(freshCommand?.commandId ?? null);

  const load = useCallback(async () => {
    if (!deviceId) { setCaptures([]); return; }
    try {
      const res = await fleetSupportApi.listCommands(deviceId, LIST_LIMIT);
      const list = (res?.commands || []).filter(isCapture);
      setCaptures((prev) => {
        // Keep a capture the header just created even if the read beat the
        // write to the index — it must not blink out of the list.
        const known = new Set(list.map((c) => c.commandId));
        const carried = (prev || []).filter((c) => !known.has(c.commandId) && !isTerminalState(c.state)
          && Date.now() - c.createdAt < POLL_WINDOW_MS);
        return [...carried, ...list];
      });
      setError(null);
    } catch (e: any) {
      setError(e?.message || 'Could not load captures');
      setCaptures((prev) => prev ?? []);
    }
  }, [deviceId]);

  useEffect(() => { void load(); }, [load]);

  // A capture requested from the header shows at once, selected.
  useEffect(() => {
    if (!freshCommand || !isCapture(freshCommand)) return;
    setCaptures((prev) => {
      const rest = (prev || []).filter((c) => c.commandId !== freshCommand.commandId);
      return [freshCommand, ...rest];
    });
    setSelectedId(freshCommand.commandId);
  }, [freshCommand]);

  // Poll every 3 s while a recent capture is still waiting on the till.
  const waiting = (captures || []).some((c) => !isTerminalState(c.state) && Date.now() - c.createdAt < POLL_WINDOW_MS);
  useEffect(() => {
    if (!waiting) return;
    const t = window.setTimeout(() => { void load(); }, POLL_MS);
    return () => window.clearTimeout(t);
  }, [waiting, captures, load]);

  const selected = useMemo(() => {
    if (!captures?.length) return null;
    return captures.find((c) => c.commandId === selectedId) || captures[0];
  }, [captures, selectedId]);

  if (!deviceId) {
    return (
      <section className="rounded-2xl border border-stone-200 bg-white p-5">
        <h2 className="text-[14.5px] font-semibold text-stone-900">No logs</h2>
        <p className="mt-1 text-[13px] text-stone-500">This row has never checked in, so there is no till to ask.</p>
      </section>
    );
  }

  return (
    <div className="grid items-start gap-4 lg:grid-cols-[300px_minmax(0,1fr)]">
      <section className="min-w-0 rounded-2xl border border-stone-200 bg-white">
        <div className="flex items-center justify-between gap-2 border-b border-stone-200 px-4 py-3">
          <h2 className="text-[14px] font-semibold text-stone-900">Captures</h2>
          <Pill tone="mute">kept 7 days</Pill>
        </div>
        {error && (
          <p role="alert" className="mx-3 mt-3 rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-[12px] text-rose-700">
            Could not load captures — {error}
          </p>
        )}
        {captures === null ? (
          <RubyLoader variant="section" label="Loading captures…" />
        ) : captures.length === 0 ? (
          <p className="px-4 py-6 text-[13px] text-stone-500">
            No captures yet. Press <b className="text-stone-800">Collect logs</b> or <b className="text-stone-800">Screenshot</b> at
            the top — the till uploads over its live connection, usually in under 10 seconds.
          </p>
        ) : (
          <div role="listbox" aria-label="Log captures" className="grid gap-1 p-1.5">
            {captures.map((c) => {
              const pill = capturePill(c);
              const on = selected?.commandId === c.commandId;
              const size = captureSize(c);
              const failed = c.state === 'failed' || c.state === 'expired' || c.state === 'cancelled';
              return (
                <button
                  key={c.commandId}
                  type="button"
                  role="option"
                  aria-selected={on}
                  onClick={() => setSelectedId(c.commandId)}
                  className={`grid min-h-[44px] gap-0.5 rounded-xl border px-3 py-2.5 text-left ${
                    on ? 'border-rose-200 bg-[#FFF1F3]' : 'border-transparent hover:bg-stone-50'
                  }`}
                >
                  <span className="flex items-center justify-between gap-2 text-[13px] font-semibold text-stone-900">
                    <span className="truncate">{KIND_LABEL[c.action] || c.action} · {formatWhen(c.createdAt) || '—'}</span>
                    <Pill tone={pill.tone}>{pill.label}</Pill>
                  </span>
                  <span className="text-[12px] text-stone-500">
                    {[`by ${c.requestedBy}`, size, failed ? c.failureReason : null].filter(Boolean).join(' · ')}
                  </span>
                </button>
              );
            })}
          </div>
        )}
      </section>

      <section className="min-w-0 overflow-hidden rounded-2xl border border-stone-200 bg-white">
        {selected ? (
          <CaptureViewer key={selected.commandId} row={row} capture={selected} />
        ) : (
          <p className="px-5 py-10 text-center text-[13px] text-stone-500">Pick a capture to read it here.</p>
        )}
      </section>
    </div>
  );
}

// ---------------------------------------------------------------------------
// The viewer
// ---------------------------------------------------------------------------

function MetaBar({ capture }: { capture: FleetCommand }) {
  const size = captureSize(capture);
  return (
    <div className="flex flex-wrap gap-x-4 gap-y-1 border-b border-stone-200 bg-stone-50 px-3 py-2 text-[12px] text-stone-500">
      <span>{KIND_LABEL[capture.action]} · {formatWhen(capture.completedAt ?? capture.createdAt)}</span>
      <span>Requested by {capture.requestedBy}</span>
      {size && <span>{size}</span>}
      <span>Deleted after 7 days</span>
    </div>
  );
}

function Notice({ children, tone = 'mute' }: { children: React.ReactNode; tone?: 'mute' | 'bad' }) {
  return (
    <div className={`px-5 py-10 text-center text-[13px] ${tone === 'bad' ? 'text-rose-700' : 'text-stone-500'}`}>{children}</div>
  );
}

function CaptureViewer({ row, capture }: { row: FleetRow; capture: FleetCommand }) {
  if (capture.state === 'queued' || capture.state === 'sent' || capture.state === 'acked') {
    const offline = capture.state === 'queued' && !capture.sentAt;
    return (
      <>
        <MetaBar capture={capture} />
        <Notice>
          <div className="mb-3 flex justify-center"><RubyLoader variant="inline" size={32} label="Waiting for the till" /></div>
          {offline
            ? 'The till is offline. It gets this request the moment it reconnects (the request waits 24 hours).'
            : 'Waiting for the till to upload… it usually takes under 10 seconds while the till is online.'}
        </Notice>
      </>
    );
  }
  if (capture.state !== 'done') {
    const why = capture.failureReason
      || (capture.state === 'expired' ? 'The till did not answer in time' : capture.state === 'cancelled' ? 'Cancelled' : 'The till could not complete this');
    return (
      <>
        <MetaBar capture={capture} />
        <Notice tone="bad">{why}. Press {capture.action === 'capture_screen' ? 'Screenshot' : 'Collect logs'} to try again.</Notice>
      </>
    );
  }
  if (isExpiredCapture(capture)) {
    return (
      <>
        <MetaBar capture={capture} />
        <Notice>Expired — S3 keeps captures 7 days.</Notice>
      </>
    );
  }
  return capture.action === 'capture_screen'
    ? <ScreenViewer row={row} capture={capture} />
    : <LogViewer row={row} capture={capture} />;
}

type Fetched<T> = { status: 'loading' } | { status: 'ready'; value: T } | { status: 'gone' } | { status: 'error'; message: string };

/** Signed link → file text. A 403/404 from S3 means the object is gone. */
async function fetchArtifactText(commandId: string, deviceId: string): Promise<Fetched<string>> {
  const { url } = await fleetSupportApi.getArtifact(commandId, deviceId, 'logcat.txt');
  const res = await fetch(url);
  if (res.status === 403 || res.status === 404) return { status: 'gone' };
  if (!res.ok) return { status: 'error', message: `The file could not be read (HTTP ${res.status})` };
  return { status: 'ready', value: await res.text() };
}

/** Text with every case-insensitive hit of `term` wrapped in <mark>. */
function Highlight({ text, term }: { text: string; term: string }) {
  if (!term) return <>{text}</>;
  const lower = text.toLowerCase();
  const out: React.ReactNode[] = [];
  let i = 0;
  let k = 0;
  for (;;) {
    const at = lower.indexOf(term, i);
    if (at < 0) break;
    if (at > i) out.push(text.slice(i, at));
    out.push(<mark key={k++} className="rounded-sm bg-yellow-300 text-stone-900">{text.slice(at, at + term.length)}</mark>);
    i = at + term.length;
  }
  out.push(text.slice(i));
  return <>{out}</>;
}

const LEVELS: { id: LevelFilter; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'errors', label: 'Errors' },
  { id: 'warn', label: 'Warn+' },
];

function LogViewer({ row, capture }: { row: FleetRow; capture: FleetCommand }) {
  const [file, setFile] = useState<Fetched<string>>({ status: 'loading' });
  const [term, setTerm] = useState('');
  const [level, setLevel] = useState<LevelFilter>('all');
  const [wrap, setWrap] = useState(true);
  const [limit, setLimit] = useState(RENDER_STEP);
  const deferredTerm = useDeferredValue(term.trim().toLowerCase());

  useEffect(() => {
    let live = true;
    setFile({ status: 'loading' });
    fetchArtifactText(capture.commandId, capture.deviceId)
      .then((f) => { if (live) setFile(f); })
      .catch((e: any) => { if (live) setFile({ status: 'error', message: e?.message || 'Could not fetch the log file' }); });
    return () => { live = false; };
  }, [capture.commandId, capture.deviceId]);

  const text = file.status === 'ready' ? file.value : '';
  const lines = useMemo(() => parseLogcat(text), [text]);
  const matching = useMemo(() => filterLines(lines, level, deferredTerm), [lines, level, deferredTerm]);
  useEffect(() => { setLimit(RENDER_STEP); }, [level, deferredTerm]);
  const shown = matching.slice(0, limit);

  const fileName = captureFileName(row, capture.completedAt ?? capture.createdAt, 'logcat', 'txt');

  const onCopy = async () => {
    const ok = await copyText(matching.map((l) => l.raw).join('\n'));
    if (ok) toastService.success(`Copied ${matching.length.toLocaleString()} line${matching.length === 1 ? '' : 's'}`);
    else toastService.error('Copy is blocked here — use Download .txt instead');
  };
  const onDownload = () => {
    saveFile(new Blob([text], { type: 'text/plain;charset=utf-8' }), fileName);
  };

  const ready = file.status === 'ready';

  return (
    <>
      <div className="flex flex-wrap items-center gap-2 border-b border-stone-200 px-3 py-2.5">
        <label className="flex min-h-[44px] flex-[1_1_200px] items-center gap-2 rounded-xl border border-stone-300 bg-white px-3">
          <Search size={15} className="shrink-0 text-stone-400" aria-hidden="true" />
          <input
            value={term}
            onChange={(e) => setTerm(e.target.value)}
            placeholder="Filter lines (e.g. printer, 503, Card)"
            aria-label="Filter lines"
            autoComplete="off"
            className="min-w-0 flex-1 bg-transparent text-[13px] text-stone-900 outline-none placeholder:text-stone-400"
          />
        </label>
        <div role="group" aria-label="Level" className="inline-flex rounded-full border border-stone-300 p-0.5">
          {LEVELS.map((l) => (
            <button
              key={l.id}
              type="button"
              aria-pressed={level === l.id}
              onClick={() => setLevel(l.id)}
              className={`min-h-[40px] rounded-full px-3 text-[12px] font-semibold ${
                level === l.id ? 'bg-stone-900 text-white' : 'text-stone-500 hover:text-stone-900'
              }`}
            >
              {l.label}
            </button>
          ))}
        </div>
        <button
          type="button"
          aria-pressed={wrap}
          onClick={() => setWrap((w) => !w)}
          title={wrap ? 'Long lines wrap' : 'Long lines scroll sideways'}
          className={`inline-flex min-h-[44px] items-center gap-1.5 rounded-xl border px-3 text-[12.5px] font-semibold ${
            wrap ? 'border-stone-900 text-stone-900' : 'border-stone-300 text-stone-500'
          }`}
        >
          <WrapText size={15} aria-hidden="true" /> Wrap
        </button>
        <button
          type="button"
          onClick={onCopy}
          disabled={!ready}
          className="inline-flex min-h-[44px] items-center gap-1.5 rounded-xl border border-stone-300 bg-white px-3 text-[12.5px] font-semibold text-stone-800 hover:border-[#DC143C] hover:text-[#DC143C] disabled:opacity-45"
        >
          <Copy size={15} aria-hidden="true" /> Copy
        </button>
        <button
          type="button"
          onClick={onDownload}
          disabled={!ready}
          className="inline-flex min-h-[44px] items-center gap-1.5 rounded-xl bg-[#DC143C] px-4 text-[12.5px] font-semibold text-white hover:bg-[#9F1239] disabled:opacity-45"
        >
          <Download size={15} aria-hidden="true" /> Download .txt
        </button>
      </div>
      <MetaBar capture={capture} />

      {file.status === 'loading' && <RubyLoader variant="section" label="Reading the log file…" />}
      {file.status === 'gone' && <Notice>Expired — S3 keeps captures 7 days.</Notice>}
      {file.status === 'error' && (
        <Notice tone="bad"><AlertTriangle size={15} className="mr-1 inline" aria-hidden="true" />{file.message}</Notice>
      )}
      {ready && (
        <>
          <div className="flex flex-wrap items-center gap-x-3 border-b border-stone-200 px-3 py-1.5 text-[11.5px] text-stone-500">
            <FileText size={13} aria-hidden="true" />
            <span>
              {matching.length === lines.length
                ? `${lines.length.toLocaleString()} lines`
                : `${matching.length.toLocaleString()} of ${lines.length.toLocaleString()} lines match`}
            </span>
            {matching.length > shown.length && <span>· showing the first {shown.length.toLocaleString()}</span>}
          </div>
          <div
            tabIndex={0}
            aria-label="Log lines"
            data-testid="log-lines"
            className="max-h-[560px] overflow-auto bg-[#0C0A09] py-2 font-mono text-[12px] leading-[1.6] text-stone-200"
          >
            {shown.length === 0 ? (
              <div className="px-4 py-6 text-center text-stone-500">No lines match.</div>
            ) : shown.map((l) => <Line key={l.n} line={l} term={deferredTerm} wrap={wrap} />)}
            {matching.length > shown.length && (
              <div className="px-3 py-2">
                <button
                  type="button"
                  onClick={() => setLimit((n) => n + RENDER_STEP)}
                  className="min-h-[44px] rounded-xl border border-stone-700 px-4 font-sans text-[12.5px] font-semibold text-stone-200 hover:bg-stone-800"
                >
                  Show {Math.min(RENDER_STEP, matching.length - shown.length).toLocaleString()} more
                </button>
              </div>
            )}
          </div>
        </>
      )}
    </>
  );
}

function Line({ line: l, term, wrap }: { line: LogLine; term: string; wrap: boolean }) {
  const errorish = l.level === 'E' || l.level === 'F' || l.level === 'A';
  return (
    <div
      data-level={l.level ?? ''}
      className={`grid grid-cols-[48px_minmax(0,1fr)] gap-2.5 px-3 hover:bg-stone-900 ${errorish ? 'bg-[#2A0E0E]' : ''} ${
        wrap ? 'whitespace-pre-wrap break-words' : 'w-max min-w-full whitespace-pre'
      }`}
    >
      <span className="select-none text-right text-stone-600">{l.n}</span>
      {l.level ? (
        <span>
          <span className="text-stone-500">{l.ts} </span>
          <span className={`font-semibold ${levelClass(l.level)}`}>{l.level}</span>{' '}
          <span className={errorish ? 'text-red-200' : ''}><Highlight text={`${l.tag}: ${l.msg}`} term={term} /></span>
        </span>
      ) : (
        <span className="text-stone-300"><Highlight text={l.raw} term={term} /></span>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Screenshots
// ---------------------------------------------------------------------------

function ScreenViewer({ row, capture }: { row: FleetRow; capture: FleetCommand }) {
  const [shots, setShots] = useState<Fetched<{ name: 'main' | 'panel'; url: string }[]>>({ status: 'loading' });

  useEffect(() => {
    let live = true;
    setShots({ status: 'loading' });
    Promise.all([
      fleetSupportApi.getArtifact(capture.commandId, capture.deviceId, 'screen-main.png'),
      // A single-screen till has no customer panel — a 404 here is normal.
      fleetSupportApi.getArtifact(capture.commandId, capture.deviceId, 'screen-panel.png').catch(() => null),
    ])
      .then(([main, panel]) => {
        if (!live) return;
        const list: { name: 'main' | 'panel'; url: string }[] = [{ name: 'main', url: main.url }];
        if (panel?.url) list.push({ name: 'panel', url: panel.url });
        setShots({ status: 'ready', value: list });
      })
      .catch((e: any) => { if (live) setShots({ status: 'error', message: e?.message || 'Could not fetch the screenshot' }); });
    return () => { live = false; };
  }, [capture.commandId, capture.deviceId]);

  const download = async (name: 'main' | 'panel', url: string) => {
    const base = captureFileName(row, capture.completedAt ?? capture.createdAt, 'screen', 'png');
    const fileName = name === 'panel' ? base.replace(/\.png$/, '-customer.png') : base;
    try {
      const res = await fetch(url);
      if (!res.ok) throw new Error(String(res.status));
      saveFile(await res.blob(), fileName);
    } catch {
      // The signed link still works in a new tab even where a cross-origin
      // fetch is refused — never leave the operator with nothing.
      window.open(url, '_blank', 'noopener');
    }
  };

  return (
    <>
      <MetaBar capture={capture} />
      {shots.status === 'loading' && <RubyLoader variant="section" label="Fetching the screenshot…" />}
      {shots.status === 'error' && <Notice tone="bad">{shots.message}</Notice>}
      {shots.status === 'ready' && (
        <div className="grid gap-4 p-4 md:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
          {shots.value.map((s) => (
            <figure key={s.name} className="grid gap-2">
              <ShimmerImage
                src={s.url}
                alt={s.name === 'main' ? 'Cashier screen' : 'Customer screen'}
                objectFit="contain"
                variant="light"
                aspectRatio={s.name === 'main' ? '16 / 10' : '4 / 3'}
                className="w-full rounded-xl border border-stone-200 bg-stone-900"
                fallbackIcon={<ImageIcon size={28} className="text-stone-400" />}
              />
              <figcaption className="flex items-center justify-between gap-2 text-[12px] text-stone-500">
                <span>{s.name === 'main' ? 'Cashier screen' : 'Customer screen'}</span>
                <button
                  type="button"
                  onClick={() => { void download(s.name, s.url); }}
                  className="inline-flex min-h-[44px] items-center gap-1.5 rounded-xl border border-stone-300 bg-white px-3 text-[12.5px] font-semibold text-stone-800 hover:border-[#DC143C] hover:text-[#DC143C]"
                >
                  <Download size={15} aria-hidden="true" /> Download .png
                </button>
              </figcaption>
            </figure>
          ))}
        </div>
      )}
      <p className="border-t border-stone-100 px-4 py-2 text-[11.5px] text-stone-400">
        Captured {relativeTime(capture.completedAt ?? capture.createdAt) || '—'}.
      </p>
    </>
  );
}
